/**
 * Service de génération des produits Stripe pour la facturation des frais de mandat
 * 
 * Ce service transforme les calculs d'heures facturables en produits Stripe
 * pour permettre le paiement par les bénéficiaires
 */

import stripeApiService from './stripeApiService';
import clientPaymentService from './clientPaymentService';
import { db } from '../firebase';
import { doc, setDoc, getDoc, getDocs, addDoc, collection, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';

class StripeBillingService {
  
  /**
   * Génère un produit Stripe pour la facturation des frais de mandat
   * @param {string} clientId - ID du client
   * @param {Object} periode - Période de facturation
   * @param {Object} calculHeures - Calcul des heures facturables
   * @param {Object} options - Options supplémentaires
   * @returns {Object} Produit Stripe généré avec détails
   */
  async genererFactureMandat(clientId, periode, calculHeures, options = {}) {
    try {
      console.log(`💰 Génération facture mandat pour client ${clientId}`, {
        periode: periode.libelle,
        totalHeures: calculHeures.totalHeures,
        montantTotal: calculHeures.montantTotal
      });

      // Récupérer les informations du client
      const client = await this.getClientInfo(clientId);
      if (!client) {
        throw new Error(`Client ${clientId} non trouvé`);
      }

      // Valider que le calcul contient des heures
      if (calculHeures.totalHeures <= 0) {
        throw new Error('Aucune heure à facturer pour cette période');
      }

      // Préparer les données du produit
      const produitData = await this.preparerDonneesProduit(
        client, 
        periode, 
        calculHeures, 
        options
      );

      // Créer le produit dans Stripe
      const produit = await stripeApiService.createProduct(produitData.product);

      // Créer le prix associé
      // NOTE: stripeApiService.createPrice multiplie déjà par 100, donc on passe le montant en euros
      const prix = await stripeApiService.createPrice({
        productId: produit.id,
        unitAmount: produitData.montantTotal, // En euros (sera converti en centimes par stripeApiService)
        currency: 'eur',
        nickname: produitData.nomPrix
      });

      // Générer l'ID de facture AVANT de créer les enregistrements
      const factureId = await this.genererIdFacture(clientId, periode);

      // Assigner automatiquement le produit au client
      const assignment = await this.assignerProduitAuClient({
        clientId,
        client,
        produitStripe: produit,
        prixStripe: prix,
        calculHeures,
        periode,
        options
      });

      // Créer la facture dans le système existant avec l'ID généré
      const factureClient = await this.creerFactureClient({
        clientId,
        produitStripe: produit,
        prixStripe: prix,
        calculHeures,
        assignment,
        periode,
        options,
        invoiceId: factureId  // Passer l'ID de facture comptable
      });

      // Enregistrer la facture dans Firestore pour historique
      const factureRecord = await this.enregistrerFacture({
        clientId,
        client,
        periode,
        calculHeures,
        produitStripe: produit,
        prixStripe: prix,
        assignment,
        factureClient,
        options,
        invoiceId: factureId  // Passer l'ID pour utilisation
      });

      console.log('✅ Facture Stripe générée avec succès:', {
        produitId: produit.id,
        prixId: prix.id,
        factureId: factureRecord.id,
        assignmentId: assignment.id,
        factureClientId: factureClient.id,
        montant: produitData.montantTotal
      });

      return {
        produit,
        prix,
        facture: factureRecord,
        assignment,
        factureClient,
        calculHeures,
        client,
        periode
      };

    } catch (error) {
      console.error('❌ Erreur génération facture mandat:', error);
      throw new Error(`Échec de la génération de la facture: ${error.message}`);
    }
  }

  /**
   * Prépare les données pour la création du produit Stripe
   * @param {Object} client - Informations du client
   * @param {Object} periode - Période de facturation
   * @param {Object} calculHeures - Calcul des heures
   * @param {Object} options - Options supplémentaires
   * @returns {Object} Données du produit
   */
  async preparerDonneesProduit(client, periode, calculHeures, options = {}) {
    const nomClient = this.formaterNomClient(client);
    const libellePeriode = this.formaterPeriodeFacturation(periode);

    // Générer un identifiant unique pour la facture
    const factureId = await this.genererIdFacture(client.id, periode);
    
    const nomProduit = `Frais mandat - ${nomClient} - ${libellePeriode}`;
    const description = this.genererDescriptionFacture(calculHeures, periode);
    const nomPrix = `Mandat ${libellePeriode}`;

    // Métadonnées détaillées
    const metadata = {
      category: "mandate_fees_realtime",
      target: "beneficiary",
      client_id: client.id,
      facture_id: factureId,
      periode: `${periode.debut.getFullYear()}-${periode.debut.getMonth() + 1}`,
      heures_totales: calculHeures.totalHeures,
      frais_mandat_unitaire: calculHeures.fraisMandatUnitaire,
      montant_total: calculHeures.montantTotal,
      nombre_rendez_vous: calculHeures.nombreRendezVous,
      type_calcul: "reel_rendez_vous",
      contrat_id: calculHeures.contrat?.id,
      volume_hebdo: calculHeures.contrat?.volumeHebdo,
      base_rate_type: calculHeures.contrat?.baseRateType,
      date_generation: new Date().toISOString(),
      ...options.metadata
    };

    return {
      product: {
        name: nomProduit,
        description: description,
        metadata: metadata,
        active: true
      },
      montantTotal: calculHeures.montantTotal,
      nomPrix: nomPrix,
      factureId: factureId
    };
  }

  /**
   * Formate le nom du client pour l'affichage
   * @param {Object} client - Informations du client
   * @returns {string} Nom formaté
   */
  formaterNomClient(client) {
    if (client.firstName && client.lastName) {
      return `${client.firstName} ${client.lastName}`;
    } else if (client.nom) {
      return client.nom;
    } else if (client.name) {
      return client.name;
    }
    return `Client ${client.id}`;
  }

  /**
   * Formate la période pour l'affichage
   * @param {Object} periode - Période de facturation
   * @returns {string} Période formatée
   */
  formaterPeriodeFacturation(periode) {
    if (periode.libelle) {
      return periode.libelle;
    }

    const debut = periode.debut;
    const fin = periode.fin;

    if (debut.getMonth() === fin.getMonth() && debut.getFullYear() === fin.getFullYear()) {
      // Même mois
      return debut.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } else {
      // Période sur plusieurs mois - afficher uniquement le mois de fin (mois facturé)
      return fin.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  }

  /**
   * Génère un identifiant unique pour la facture
   * Format: FACT-YYYY-MM-NNNN (ex: FACT-2025-10-0001)
   * Le numéro est global et incrémentiel, ne se réinitialise jamais
   * @param {string} clientId - ID du client (non utilisé mais conservé pour compatibilité)
   * @param {Object} periode - Période de facturation
   * @returns {Promise<string>} ID de facture
   */
  async genererIdFacture(clientId, periode) {
    try {
      // 1. Récupérer la dernière facture pour obtenir le dernier numéro séquentiel
      const facturesRef = collection(db, 'stripeBillingInvoices');
      const q = query(facturesRef, orderBy('id', 'desc'), limit(1));
      const snapshot = await getDocs(q);

      // 2. Extraire le dernier numéro séquentiel utilisé
      let dernierNumero = 0;
      if (!snapshot.empty) {
        const dernierID = snapshot.docs[0].data().id;
        // Extraire le numéro de FACT-2025-10-0003 → 3
        const match = dernierID.match(/FACT-\d{4}-\d{2}-(\d+)$/);
        if (match) {
          dernierNumero = parseInt(match[1], 10);
        }
      }

      // 3. Incrémenter le compteur global
      const nouveauNumero = dernierNumero + 1;

      // 4. Formater l'ID avec année-mois de la période
      const annee = periode.debut.getFullYear();
      const mois = String(periode.debut.getMonth() + 1).padStart(2, '0');
      const numeroFormate = String(nouveauNumero).padStart(4, '0');

      const factureId = `FACT-${annee}-${mois}-${numeroFormate}`;

      console.log(`📋 Génération ID facture: ${factureId} (compteur global: ${nouveauNumero})`);

      return factureId;

    } catch (error) {
      console.error('❌ Erreur génération ID facture:', error);
      // Fallback: utiliser un timestamp si la requête échoue
      const timestamp = new Date().getTime();
      const annee = periode.debut.getFullYear();
      const mois = String(periode.debut.getMonth() + 1).padStart(2, '0');
      return `FACT-${annee}-${mois}-${timestamp}`;
    }
  }

  /**
   * Génère la description de la facture
   * @param {Object} calculHeures - Calcul des heures
   * @param {Object} periode - Période de facturation
   * @returns {string} Description formatée
   */
  genererDescriptionFacture(calculHeures, periode) {
    const detailsHeures = `${calculHeures.totalHeures}h réalisées sur ${calculHeures.nombreRendezVous} rendez-vous`;
    const detailsTarif = `Frais mandat: ${calculHeures.fraisMandatUnitaire}€/h`;
    const periodeDesc = `Période: ${this.formaterPeriodeFacturation(periode)}`;
    
    return `${detailsHeures}. ${detailsTarif}. ${periodeDesc}. Facture générée automatiquement par OMAD SERVICES.`;
  }

  /**
   * Récupère les informations d'un client
   * @param {string} clientId - ID du client
   * @returns {Object} Informations du client
   */
  async getClientInfo(clientId) {
    try {
      // Essayer différentes collections
      const collections = ['clients', 'beneficiaries', 'users'];
      
      for (const collectionName of collections) {
        try {
          const clientRef = doc(db, collectionName, clientId);
          const clientDoc = await getDoc(clientRef);
          
          if (clientDoc.exists()) {
            console.log(`👤 Client trouvé dans ${collectionName}:`, clientId);
            return { id: clientId, ...clientDoc.data() };
          }
        } catch (error) {
          console.warn(`⚠️ Collection ${collectionName} non accessible:`, error.message);
        }
      }
      
      throw new Error(`Client ${clientId} non trouvé dans les collections standards`);
      
    } catch (error) {
      console.error('❌ Erreur récupération client:', error);
      throw error;
    }
  }

  /**
   * Assigner automatiquement le produit au client via clientPaymentService
   * @param {Object} data - Données d'attribution
   * @returns {Object} Attribution créée
   */
  async assignerProduitAuClient(data) {
    try {
      const {
        clientId,
        client,
        produitStripe,
        prixStripe,
        calculHeures,
        periode,
        options
      } = data;

      console.log(`🔄 Attribution automatique du produit ${produitStripe.id} au client ${clientId}`);

      // Préparer les options d'attribution
      const assignmentOptions = {
        assignedBy: options.createdBy || 'system',
        notes: `Facture mandat ${this.formaterPeriodeFacturation(periode)} - ${calculHeures.totalHeures}h réalisées`,
        customPrice: Math.round(calculHeures.montantTotal * 100), // En centimes
        skipInvoiceCreation: true, // Ne pas créer de facture automatique, on la crée manuellement après
        metadata: {
          type: 'mandate_fees',
          periode: periode.libelle,
          heures: calculHeures.totalHeures,
          rendezVous: calculHeures.nombreRendezVous,
          fraisUnitaire: calculHeures.fraisMandatUnitaire
        }
      };

      // Utiliser le service existant pour assigner le produit
      const assignment = await clientPaymentService.assignProductToClient(
        clientId,
        produitStripe.id,
        assignmentOptions
      );

      console.log('✅ Produit assigné automatiquement:', assignment.id);
      return assignment;

    } catch (error) {
      console.error('❌ Erreur attribution automatique:', error);
      throw new Error(`Échec de l'attribution automatique: ${error.message}`);
    }
  }

  /**
   * Créer la facture dans le système clientInvoices existant
   * @param {Object} data - Données de la facture
   * @returns {Object} Facture créée
   */
  async creerFactureClient(data) {
    try {
      const {
        clientId,
        produitStripe,
        prixStripe,
        calculHeures,
        assignment,
        periode,
        options,
        invoiceId  // ID de facture comptable à utiliser
      } = data;

      console.log(`📄 Création facture client pour produit ${produitStripe.id}`);

      // Calculer les dates
      const now = new Date();
      const dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 jours

      // Préparer les données de la facture
      const montantCentimes = Math.round(calculHeures.montantTotal * 100); // En centimes

      const factureData = {
        invoiceNumber: invoiceId,  // Utiliser l'ID comptable au lieu de générer FAC-XXXXXX
        clientId: clientId,
        productId: produitStripe.id,
        productName: produitStripe.name,
        productDescription: produitStripe.description,
        amount: montantCentimes,
        currency: 'eur',
        status: 'pending',
        createdAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(dueDate),
        billingCycle: 'one_time',
        stripePriceId: prixStripe.id,
        paymentIntentId: null,
        paidAt: null,
        paymentMethod: null,
        metadata: {
          type: 'mandate_fees',
          periode: periode?.libelle || this.formaterPeriodeFacturation(periode),
          heures: calculHeures.totalHeures,
          rendezVous: calculHeures.nombreRendezVous,
          fraisUnitaire: calculHeures.fraisMandatUnitaire,
          generatedBy: 'stripe_billing_service',
          category: 'mandate_fees_realtime',
          target: 'beneficiary',
          assignmentId: assignment.id
        }
      };

      // Créer directement la facture dans clientInvoices au lieu d'utiliser createInvoiceForProduct
      const docRef = await addDoc(collection(db, 'clientInvoices'), factureData);
      console.log(`✅ Facture client créée: ${invoiceId}`);

      return { id: docRef.id, ...factureData };

    } catch (error) {
      console.error('❌ Erreur création facture client:', error);
      throw new Error(`Échec de la création de la facture: ${error.message}`);
    }
  }

  /**
   * Enregistre la facture dans Firestore pour historique
   * @param {Object} data - Données de la facture
   * @returns {Object} Enregistrement de la facture
   */
  async enregistrerFacture(data) {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const {
          clientId,
          client,
          periode,
          calculHeures,
          produitStripe,
          prixStripe,
          options,
          invoiceId  // ID déjà généré, passé depuis genererFactureMandat
        } = data;

        const factureId = invoiceId || await this.genererIdFacture(clientId, periode);

        // Vérifier si l'ID existe déjà (protection contre les doublons)
        const factureRef = doc(db, 'stripeBillingInvoices', factureId);
        const existingDoc = await getDoc(factureRef);

        if (existingDoc.exists()) {
          console.warn(`⚠️ Facture ${factureId} existe déjà, retry ${retryCount + 1}/${maxRetries}`);
          retryCount++;
          // Attendre un peu avant de réessayer
          await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
          continue;
        }

        const factureData = {
          id: factureId,
          clientId: clientId,
          client: {
            id: client.id,
            nom: this.formaterNomClient(client),
            email: client.email || '',
            telephone: client.phone || client.telephone || client.phoneNumber || ''
          },
          periode: {
            debut: periode.debut instanceof Date ? Timestamp.fromDate(periode.debut) : periode.debut,
            fin: periode.fin instanceof Date ? Timestamp.fromDate(periode.fin) : periode.fin,
            libelle: this.formaterPeriodeFacturation(periode)
          },
          calcul: {
            totalHeures: calculHeures.totalHeures || 0,
            nombreRendezVous: calculHeures.nombreRendezVous || 0,
            fraisMandatUnitaire: calculHeures.fraisMandatUnitaire || 0,
            montantTotal: calculHeures.montantTotal || 0,
            contrat: calculHeures.contrat || null,
            rendezVousDetails: this.nettoyerRendezVousDetails(calculHeures.rendezVousDetails || [])
          },
          stripe: {
            produitId: produitStripe.id,
            prixId: prixStripe.id,
            metadata: produitStripe.metadata || {}
          },
          statut: 'generée',
          dateGeneration: Timestamp.now(),
          options: options || {},
          createdBy: (options && options.createdBy) || 'system'
        };

        // Enregistrer dans la collection des factures
        console.log('💾 Tentative d\'enregistrement de la facture dans stripeBillingInvoices...');
        console.log('📝 Données de la facture:', JSON.stringify(factureData, null, 2));
        console.log('🆔 ID de la facture:', factureId);

        await setDoc(factureRef, factureData);

        console.log('✅ Facture enregistrée avec succès dans Firestore:', factureId);
        console.log('📍 Collection: stripeBillingInvoices');
        return factureData;

      } catch (error) {
        console.error('❌ Erreur enregistrement facture:', error);
        retryCount++;

        if (retryCount >= maxRetries) {
          console.error('❌ Échec après 3 tentatives');
          // Ne pas bloquer le processus si l'enregistrement échoue
          return { error: error.message };
        }

        // Attendre avant de réessayer
        await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
      }
    }

    return { error: 'Échec de l\'enregistrement après plusieurs tentatives' };
  }

  /**
   * Récupère l'historique des factures d'un client
   * @param {string} clientId - ID du client
   * @param {number} limit - Nombre maximum de factures à récupérer
   * @returns {Array} Liste des factures
   */
  async getHistoriqueFactures(clientId, limit = 10) {
    try {
      // Cette fonction nécessiterait une collection dédiée avec des index
      // Pour l'instant, retourne un message indiquant que c'est à implémenter
      console.log(`📋 Récupération historique factures client ${clientId}`);
      
      return {
        message: 'Fonctionnalité à implémenter avec une collection dédiée',
        clientId,
        limit
      };

    } catch (error) {
      console.error('❌ Erreur récupération historique:', error);
      throw error;
    }
  }

  /**
   * Nettoie les détails des rendez-vous en supprimant les valeurs undefined
   * @param {Array} rendezVousDetails - Détails des rendez-vous
   * @returns {Array} Détails nettoyés
   */
  nettoyerRendezVousDetails(rendezVousDetails) {
    if (!Array.isArray(rendezVousDetails)) {
      return [];
    }

    return rendezVousDetails.map(rdv => {
      const cleaned = {};
      Object.keys(rdv).forEach(key => {
        const value = rdv[key];
        // Ne garder que les valeurs non-undefined
        if (value !== undefined && value !== null) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }

  /**
   * Valide les données avant génération de facture
   * @param {string} clientId - ID du client
   * @param {Object} periode - Période de facturation
   * @param {Object} calculHeures - Calcul des heures
   * @returns {Object} Résultat de validation
   */
  validerDonneesFacturation(clientId, periode, calculHeures) {
    const erreurs = [];

    if (!clientId) {
      erreurs.push('ID client requis');
    }

    if (!periode || !periode.debut || !periode.fin) {
      erreurs.push('Période invalide');
    }

    if (!calculHeures || typeof calculHeures.totalHeures !== 'number') {
      erreurs.push('Calcul des heures invalide');
    }

    if (calculHeures.totalHeures <= 0) {
      erreurs.push('Aucune heure à facturer');
    }

    if (calculHeures.montantTotal <= 0) {
      erreurs.push('Montant total invalide');
    }

    return {
      valide: erreurs.length === 0,
      erreurs
    };
  }

  /**
   * Supprime une facture et son produit Stripe associé
   * @param {string} invoiceId - ID de la facture
   * @returns {Object} Résultat de la suppression
   */
  async supprimerFacture(invoiceId, reason = '') {
    try {
      console.log(`🗑️ Suppression de la facture ${invoiceId}`);

      // Récupérer la facture
      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      const facture = factureDoc.data();

      // ⛔ PROTECTION : Empêcher la suppression des factures payées
      if (facture.statut === 'payée') {
        throw new Error('Impossible de supprimer une facture payée. Pour des raisons comptables et légales, les factures payées ne peuvent pas être supprimées. Veuillez contacter le support si nécessaire.');
      }

      // Valider que la raison est fournie
      if (!reason || !reason.trim()) {
        throw new Error('Une raison de suppression est obligatoire');
      }

      // Supprimer le produit Stripe si présent
      if (facture.stripe?.produitId) {
        try {
          // D'abord supprimer le prix associé
          if (facture.stripe?.prixId) {
            console.log(`🗑️ Suppression du prix Stripe: ${facture.stripe.prixId}`);
            try {
              await stripeApiService.deletePrice(facture.stripe.prixId);
              console.log('✅ Prix Stripe supprimé');
            } catch (priceError) {
              console.warn('⚠️ Erreur suppression prix Stripe:', priceError.message);
              // On continue même si le prix ne peut pas être supprimé
            }
          }

          // Ensuite supprimer le produit
          console.log(`🗑️ Suppression du produit Stripe: ${facture.stripe.produitId}`);
          await stripeApiService.deleteProduct(facture.stripe.produitId);
          console.log('✅ Produit Stripe supprimé');
        } catch (stripeError) {
          console.warn('⚠️ Erreur suppression produit Stripe (peut-être déjà supprimé):', stripeError.message);
          // On continue même si Stripe échoue
        }
      }

      // Supprimer l'attribution au client (clientProducts)
      if (facture.clientId && facture.stripe?.produitId) {
        try {
          // Supprimer de la collection clientProducts
          const clientProductsRef = collection(db, 'clientProducts');
          const q = query(
            clientProductsRef,
            where('clientId', '==', facture.clientId),
            where('productId', '==', facture.stripe.produitId)
          );
          const snapshot = await getDocs(q);

          for (const docSnap of snapshot.docs) {
            await setDoc(doc(db, 'clientProducts', docSnap.id), {
              ...docSnap.data(),
              deleted: true,
              dateSupression: Timestamp.now()
            });
          }

          console.log('✅ Attribution client supprimée');
        } catch (removeError) {
          console.warn('⚠️ Erreur suppression attribution:', removeError.message);
        }
      }

      // Supprimer la facture de Firestore avec raison et historique
      await setDoc(factureRef, {
        ...facture,
        deleted: true,
        dateSupression: Timestamp.now(),
        suppressionPar: 'admin',
        raisonSuppression: reason.trim(),
        historique: [
          ...(facture.historique || []),
          {
            action: 'suppression',
            date: Timestamp.now(),
            details: reason.trim(),
            statutPrecedent: facture.statut
          }
        ]
      });

      // Ou suppression complète (hard delete)
      // await deleteDoc(factureRef);

      console.log('✅ Facture supprimée avec succès');

      return {
        success: true,
        message: 'Facture supprimée avec succès',
        invoiceId,
        reason: reason.trim()
      };

    } catch (error) {
      console.error('❌ Erreur suppression facture:', error);
      throw error;
    }
  }

  /**
   * Annule une facture avec une raison
   * @param {string} invoiceId - ID de la facture
   * @param {string} reason - Raison de l'annulation
   * @returns {Object} Résultat de l'annulation
   */
  async annulerFacture(invoiceId, reason) {
    try {
      console.log(`❌ Annulation de la facture ${invoiceId}`);

      // Récupérer la facture
      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      const facture = factureDoc.data();

      // ⛔ PROTECTION : Empêcher l'annulation des factures payées
      if (facture.statut === 'payée') {
        throw new Error('Impossible d\'annuler une facture payée. Une facture payée ne peut pas être annulée pour des raisons comptables et légales. Veuillez contacter le support pour un remboursement.');
      }

      // Vérifier que la facture n'est pas déjà annulée
      if (facture.statut === 'annulée') {
        throw new Error('Cette facture est déjà annulée');
      }

      // Mettre à jour la facture
      const updates = {
        statut: 'annulée',
        raisonAnnulation: reason,
        dateAnnulation: Timestamp.now(),
        annulationPar: 'admin',
        historique: [
          ...(facture.historique || []),
          {
            action: 'annulation',
            date: Timestamp.now(),
            details: reason,
            statutPrecedent: facture.statut
          }
        ]
      };

      await setDoc(factureRef, { ...facture, ...updates });

      console.log('✅ Facture annulée avec succès');

      return {
        success: true,
        message: 'Facture annulée avec succès',
        invoiceId,
        reason
      };

    } catch (error) {
      console.error('❌ Erreur annulation facture:', error);
      throw error;
    }
  }

  /**
   * Met à jour une facture
   * @param {string} invoiceId - ID de la facture
   * @param {Object} updates - Données à mettre à jour
   * @returns {Object} Résultat de la mise à jour
   */
  async mettreAJourFacture(invoiceId, updates) {
    try {
      console.log(`✏️ Mise à jour de la facture ${invoiceId}`);

      // Récupérer la facture
      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      const facture = factureDoc.data();

      // ⛔ PROTECTION : Empêcher la modification des factures payées
      // Exception : on autorise uniquement la modification du champ 'notes'
      if (facture.statut === 'payée') {
        const champsModifies = Object.keys(updates);
        const champsAutorises = ['notes'];
        const champsNonAutorises = champsModifies.filter(champ => !champsAutorises.includes(champ));

        if (champsNonAutorises.length > 0) {
          throw new Error(`Impossible de modifier une facture payée. Seules les notes peuvent être modifiées. Champs refusés : ${champsNonAutorises.join(', ')}`);
        }
      }

      // Enregistrer dans l'historique
      const historiqueEntry = {
        action: 'modification',
        date: Timestamp.now(),
        details: 'Modification manuelle par admin',
        champModifies: Object.keys(updates)
      };

      // Fusionner les mises à jour
      const updatedData = {
        ...facture,
        ...updates,
        dateModification: Timestamp.now(),
        modificationPar: 'admin',
        historique: [
          ...(facture.historique || []),
          historiqueEntry
        ]
      };

      // Mettre à jour dans Firestore
      await setDoc(factureRef, updatedData);

      console.log('✅ Facture mise à jour avec succès');

      return {
        success: true,
        message: 'Facture mise à jour avec succès',
        invoiceId,
        updates
      };

    } catch (error) {
      console.error('❌ Erreur mise à jour facture:', error);
      throw error;
    }
  }

  /**
   * Marque une facture comme envoyée avec historique
   * @param {string} invoiceId - ID de la facture
   * @param {string} emailDestinataire - Email du destinataire
   * @returns {Object} Résultat de la mise à jour
   */
  async marquerCommeEnvoyee(invoiceId, emailDestinataire = '') {
    try {
      console.log(`📧 Marquage facture ${invoiceId} comme envoyée`);

      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      const facture = factureDoc.data();

      const updates = {
        statut: 'envoyée',
        dateEnvoi: Timestamp.now(),
        emailEnvoye: true,
        emailDestinataire: emailDestinataire || facture.client?.email || '',
        historique: [
          ...(facture.historique || []),
          {
            action: 'envoi',
            date: Timestamp.now(),
            details: `Email envoyé à ${emailDestinataire || facture.client?.email || 'destinataire'}`,
            statutPrecedent: facture.statut
          }
        ]
      };

      await setDoc(factureRef, { ...facture, ...updates });

      console.log('✅ Facture marquée comme envoyée');

      return {
        success: true,
        message: 'Facture marquée comme envoyée',
        invoiceId
      };

    } catch (error) {
      console.error('❌ Erreur marquage envoi:', error);
      throw error;
    }
  }

  /**
   * Marque une facture comme payée avec historique
   * @param {string} invoiceId - ID de la facture
   * @param {Object} paymentDetails - Détails du paiement (montant, méthode, etc.)
   * @returns {Object} Résultat de la mise à jour
   */
  async marquerCommePayee(invoiceId, paymentDetails = {}) {
    try {
      console.log(`💰 Marquage facture ${invoiceId} comme payée`);

      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      const facture = factureDoc.data();

      const updates = {
        statut: 'payée',
        datePaiement: Timestamp.now(),
        paiementDetails: paymentDetails,
        historique: [
          ...(facture.historique || []),
          {
            action: 'paiement',
            date: Timestamp.now(),
            details: `Paiement reçu - ${paymentDetails.methode || 'Non spécifié'} - ${paymentDetails.montant || facture.calcul?.montantTotal || 0}€`,
            statutPrecedent: facture.statut
          }
        ]
      };

      await setDoc(factureRef, { ...facture, ...updates });

      console.log('✅ Facture marquée comme payée');

      return {
        success: true,
        message: 'Facture marquée comme payée',
        invoiceId
      };

    } catch (error) {
      console.error('❌ Erreur marquage paiement:', error);
      throw error;
    }
  }

  /**
   * Récupère une facture par son ID
   * @param {string} invoiceId - ID de la facture
   * @returns {Object} Données de la facture
   */
  async getFacture(invoiceId) {
    try {
      const factureRef = doc(db, 'stripeBillingInvoices', invoiceId);
      const factureDoc = await getDoc(factureRef);

      if (!factureDoc.exists()) {
        throw new Error('Facture non trouvée');
      }

      return {
        id: factureDoc.id,
        ...factureDoc.data()
      };

    } catch (error) {
      console.error('❌ Erreur récupération facture:', error);
      throw error;
    }
  }
}

// Export d'une instance unique
export default new StripeBillingService();
